<template>
  <v-container>
    <v-row>
      <!-- Header -->
      <v-col cols="12" class="pb-1">
        <div class="d-flex align-center" style="gap: 10px;">
          <v-icon color="primary">mdi-cog-outline</v-icon>
          <span class="text-h6 font-weight-medium">Configuración contable</span>
        </div>
        <div class="caption grey--text mt-1 ml-8">
          Estado de la configuración contable por sucursal
        </div>
      </v-col>

      <!-- Selector de sucursal -->
      <v-col cols="12" md="6" class="py-1">
        <v-select
          v-model="sucursalSeleccionada"
          :items="sucursales"
          item-text="nombre"
          item-value="uuid"
          label="Sucursal"
          outlined
          dense
          hide-details
          @change="fetchStatus"
        ></v-select>
      </v-col>

      <!-- Loading -->
      <v-col v-if="loading" cols="12">
        <v-skeleton-loader
          type="list-item-two-line"
          v-for="i in 5"
          :key="i"
          class="mb-1"
        />
      </v-col>

      <template v-else-if="status">
        <!-- Plan sin módulo de contabilidad -->
        <v-col v-if="planNoIncluyeModulo" cols="12">
          <v-sheet
            rounded="lg"
            class="pa-6 d-flex flex-column align-center text-center"
            color="rgba(255,152,0,0.08)"
          >
            <v-icon color="warning" large class="mb-2">mdi-lock-outline</v-icon>
            <div class="subtitle-2">Tu plan no incluye contabilidad</div>
            <div class="caption grey--text">
              Para operar con contabilidad en esta sucursal, actualizá tu plan de
              suscripción.
            </div>
          </v-sheet>
        </v-col>

        <!-- Sucursal sin contabilidad -->
        <v-col v-else-if="!status.habilitada" cols="12">
          <v-sheet
            rounded="lg"
            class="pa-6 d-flex flex-column align-center text-center"
            color="rgba(255,255,255,0.03)"
          >
            <v-icon color="grey" large class="mb-2"
              >mdi-book-off-outline</v-icon
            >
            <div class="subtitle-2">
              Esta sucursal no opera con contabilidad
            </div>
            <div class="caption grey--text mb-4">
              Las ventas, compras y egresos se registran sin generar asientos
              contables.
            </div>
            <v-btn
              color="primary"
              :loading="activando"
              @click="activarContabilidad"
            >
              <v-icon left small>mdi-book-plus-outline</v-icon>
              Activar contabilidad
            </v-btn>
          </v-sheet>
        </v-col>

        <!-- Checklist por evento -->
        <template v-else>
          <v-col cols="12" class="py-1">
            <v-sheet
              rounded="lg"
              class="pa-3 d-flex align-center"
              :color="todoListo ? 'rgba(76,175,80,0.08)' : 'rgba(255,152,0,0.08)'"
              style="gap: 10px;"
            >
              <v-icon :color="todoListo ? 'success' : 'warning'" small>
                {{ todoListo ? "mdi-check-circle-outline" : "mdi-alert-outline" }}
              </v-icon>
              <span
                class="caption font-weight-medium"
                :class="
                  todoListo
                    ? 'green--text text--lighten-1'
                    : 'orange--text text--lighten-1'
                "
              >
                {{
                  todoListo
                    ? "La configuración contable está completa."
                    : "Hay configuración contable pendiente. Los eventos requeridos pueden fallar al operar."
                }}
              </span>
            </v-sheet>
          </v-col>

          <v-col cols="12" class="pt-1">
            <v-list class="pa-0" style="background: transparent;">
              <v-list-group
                v-for="evento in eventosList"
                :key="evento.key"
                :value="!evento.listo"
                class="evento-item mb-1"
                :disabled="evento.listo"
              >
                <template v-slot:activator>
                  <v-list-item-avatar size="32" class="mr-3" style="min-width: 32px;">
                    <v-icon :color="eventoColor(evento)" small>
                      {{ eventoIcon(evento) }}
                    </v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content class="py-2">
                    <v-list-item-title class="body-2 font-weight-medium">
                      {{ evento.label }}
                      <v-chip
                        x-small
                        label
                        class="ml-2"
                        :color="evento.requerido ? 'blue-grey darken-2' : 'grey darken-3'"
                        text-color="white"
                      >
                        {{ evento.requerido ? "Requerido" : "Recomendado" }}
                      </v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle class="caption mt-1">
                      {{
                        evento.listo
                          ? "Configuración completa"
                          : `${evento.faltantes.length} pendiente${
                              evento.faltantes.length > 1 ? "s" : ""
                            }`
                      }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>

                <v-list-item
                  v-for="(faltante, i) in evento.faltantes"
                  :key="i"
                  dense
                  class="pl-12"
                >
                  <v-list-item-icon class="mr-2 my-auto">
                    <v-icon x-small :color="evento.requerido ? 'deep-orange' : 'orange'"
                      >mdi-circle-medium</v-icon
                    >
                  </v-list-item-icon>
                  <v-list-item-content class="py-1">
                    <span class="caption" style="white-space: normal;">{{
                      faltante
                    }}</span>
                  </v-list-item-content>
                </v-list-item>
              </v-list-group>
            </v-list>
          </v-col>

          <!-- Link a alertas PSP -->
          <v-col cols="12" class="pt-1">
            <v-btn text small color="primary" @click="irAAlertasPSP">
              <v-icon left small>mdi-alert-circle-outline</v-icon>
              Ver alertas contables por producto (PSP)
            </v-btn>
          </v-col>
        </template>
      </template>
    </v-row>
  </v-container>
</template>

<script>
import ContabilidadApi from "@/api/contabilidad";
import { getApiErrorMessage } from "@/util/apiError";

const EVENTOS = [
  { key: "ventas", label: "Ventas", icon: "mdi-cart-outline", requerido: true },
  { key: "compras", label: "Compras", icon: "mdi-truck-outline", requerido: true },
  { key: "egresos", label: "Egresos", icon: "mdi-cash-minus", requerido: true },
  {
    key: "inventario",
    label: "Inventario",
    icon: "mdi-clipboard-list-outline",
    requerido: true,
  },
  { key: "caja", label: "Caja", icon: "mdi-cash-register", requerido: false },
];

export default {
  name: "EmpresaContabilidadConfiguracion",
  data() {
    return {
      loading: false,
      activando: false,
      sucursales: [],
      sucursalSeleccionada: null,
      status: null,
    };
  },
  computed: {
    planNoIncluyeModulo() {
      // Mientras el flag de verificación esté apagado, "habilitada" manda y
      // modulo_en_plan es solo informativo.
      return (
        this.status &&
        this.status.modulo_en_plan === false &&
        this.status.verificacion_suscripcion_activa
      );
    },
    eventosList() {
      if (!this.status || !this.status.eventos) return [];
      return EVENTOS.filter((e) => this.status.eventos[e.key]).map((e) => ({
        ...e,
        listo: this.status.eventos[e.key].listo,
        faltantes: this.status.eventos[e.key].faltantes || [],
      }));
    },
    todoListo() {
      return this.eventosList.every((e) => e.listo);
    },
  },
  methods: {
    eventoColor(evento) {
      if (evento.listo) return "success";
      return evento.requerido ? "deep-orange" : "orange";
    },
    eventoIcon(evento) {
      if (evento.listo) return "mdi-check-circle";
      return evento.requerido ? "mdi-alert-circle" : "mdi-alert";
    },
    irAAlertasPSP() {
      this.$router.push({
        name: "empresa.sucursal.contabilidad.alertas-psp",
        params: { uuid_sucursal: this.sucursalSeleccionada },
      });
    },
    async fetchSucursales() {
      try {
        const response = await this.axios.get("/sucursales/sucursales");
        this.sucursales = response.data;
      } catch {
        this.sucursales = [];
      }
    },
    async fetchStatus(sucursalUuid = null) {
      this.loading = true;
      try {
        const { data } = await ContabilidadApi.getConfiguracionStatus(
          sucursalUuid
        );
        this.status = data;
        if (data.sucursal && data.sucursal.uuid) {
          this.sucursalSeleccionada = data.sucursal.uuid;
        }
      } catch (error) {
        this.status = null;
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          button: false,
          color: "error",
          text: getApiErrorMessage(
            error,
            "Error al obtener el estado de la configuración contable"
          ),
          icon: "mdi-alert-circle",
          timeout: 4000,
        });
      } finally {
        this.loading = false;
      }
    },
    async activarContabilidad() {
      this.activando = true;
      try {
        await ContabilidadApi.registrarSucursalContable(
          this.sucursalSeleccionada
        );
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          button: false,
          color: "success",
          text: "Contabilidad activada para la sucursal",
          icon: "mdi-check",
          timeout: 3000,
        });
        await this.fetchStatus(this.sucursalSeleccionada);
      } catch (error) {
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          button: false,
          color: "error",
          text: getApiErrorMessage(error, "No se pudo activar la contabilidad"),
          icon: "mdi-alert-circle",
          timeout: 4000,
        });
      } finally {
        this.activando = false;
      }
    },
  },
  created() {
    this.fetchSucursales();
    // Sin parámetro usa la cookie de sucursal del backend
    this.fetchStatus();
  },
};
</script>

<style scoped>
.evento-item {
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
}
</style>
